import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  FormOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {CheckCircleFilled} from '@ant-design/icons/lib';
import {Typography} from 'antd';
import {
  AccountEvent,
  AccountFieldsFragment,
  ChargeEvent,
  ChurnReason,
  MetadataConfigItem,
  MetadataItem,
  PaymentMethods,
  Processor,
  ProviderEvent,
  Representative,
  StatusReason
} from 'api/generated';
import dot from 'dot-object';
import humanize from 'humanize-string';
import {colors} from 'lib/colors';
import {formatDate, formatHumanName, formatIBAN, formatPercent} from 'lib/formaters';
import {
  churnReasonLabels,
  paymentMethodLabels,
  paymentProcessorLabels,
  statusReasonLabels
} from 'lib/labels';
import {isDefined} from 'lib/utils';
import Linkify from 'linkify-react';
import moment from 'moment';
import {AccountKeysUpdate, EventType} from 'types';

export type AccountEventWithChanged = AccountEvent & {changed?: string[]};

const {Text, Paragraph} = Typography;

const checkGreen = {
  icon: CheckCircleFilled,
  color: colors.green
};
const clockSecondary = {
  icon: ClockCircleOutlined,
  color: colors.secondary
};
const infoSecondary = {
  icon: InfoCircleOutlined,
  color: colors.secondary
};
const formSecondary = {
  icon: FormOutlined,
  color: colors.secondary
};
const closeRed = {
  icon: CloseCircleOutlined,
  color: colors.red
};
const infoRed = {
  icon: InfoCircleOutlined,
  color: colors.red
};

export const eventMapping: Record<
  string,
  {title: string; icon?: any; color?: string; theme?: 'filled'}
> = {
  [EventType.ACCOUNT_PENDING]: {
    title: 'Onboarding started',
    ...clockSecondary
  },
  [EventType.ACCOUNT_DATA_REQUESTED]: {
    title: 'Additional data requested',
    ...clockSecondary
  },
  [EventType.ACCOUNT_BUSINESS_VERIFIED]: {
    title: 'Business details verified',
    ...checkGreen
  },
  [EventType.ACCOUNT_REPRESENTATIVE_VERIFIED]: {
    title: 'Business representative verified',
    ...checkGreen
  },
  [EventType.ACCOUNT_BANK_ACCOUNT_VERIFIED]: {
    title: 'Bank account verified',
    ...checkGreen
  },
  [EventType.ACCOUNT_APPROVAL_REQUESTED]: {
    title: 'Approval requested',
    ...clockSecondary
  },
  [EventType.ACCOUNT_REVIEW_REQUESTED]: {
    title: 'Review requested',
    ...clockSecondary
  },
  [EventType.ACCOUNT_APPROVED]: {
    title: 'Account approved',
    ...checkGreen
  },
  [EventType.ACCOUNT_ACTIVATED]: {
    title: 'Account activated',
    ...checkGreen
  },
  [EventType.ACCOUNT_DATA_UPDATED]: {
    title: 'Legal data updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_IBAN_UPDATED]: {
    title: 'IBAN updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_PAYOUT_IBAN_UPDATED]: {
    title: 'Payouts IBAN updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_NOTE_UPDATED]: {
    title: 'Admin note updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_PLAN_UPDATED]: {
    title: 'Plan updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_BILLING_UPDATED]: {
    title: 'Billing updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_REJECTED]: {
    title: 'Account rejected',
    ...closeRed
  },
  [EventType.ACCOUNT_DELETED]: {
    title: 'Account deleted',
    ...closeRed
  },
  [EventType.ACCOUNT_SUSPENDED]: {
    title: 'Account suspended',
    ...closeRed
  },
  [EventType.ACCOUNT_BLOCKED]: {
    title: 'Account blocked',
    ...closeRed
  },
  [EventType.ACCOUNT_UNBLOCKED]: {
    title: 'Account unblocked',
    ...checkGreen
  },
  [EventType.ACCOUNT_REVIEW_CONFIRMED]: {
    title: 'Account review confirmed',
    ...checkGreen
  },
  [EventType.ACCOUNT_CHURNED]: {
    title: 'Account churned',
    ...infoRed
  },
  [EventType.ACCOUNT_REACTIVATED]: {
    title: 'Account reactivated',
    ...checkGreen
  },
  [EventType.ACCOUNT_PAYOUT_TERMS_UPDATED]: {
    title: 'Payout terms updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_CHURN_REASON_UPDATED]: {
    title: 'Churn reason updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_PAYOUTS_SUSPENDED]: {
    title: 'Payouts suspended',
    ...closeRed
  },
  [EventType.ACCOUNT_PAYOUTS_RESUMED]: {
    title: 'Payouts resumed',
    ...checkGreen
  },
  [EventType.ACCOUNT_PIBISI_VERIFIED]: {
    title: 'PIBISI verified',
    ...checkGreen
  },
  [EventType.ACCOUNT_PIBISI_REJECTED]: {
    title: 'PIBISI rejected',
    ...infoRed
  },
  [EventType.ACCOUNT_PIBISI_UNDER_REVIEW]: {
    title: 'PIBISI under review',
    ...clockSecondary
  },
  [EventType.ACCOUNT_REFUNDS_ENABLED]: {
    title: 'Refunds enabled',
    ...checkGreen
  },
  [EventType.ACCOUNT_REFUNDS_DISABLED]: {
    title: 'Refunds disabled',
    ...infoRed
  },
  [EventType.ACCOUNT_TAGS_UPDATED]: {
    title: 'Tags updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_EXECUTIVE_ASSIGNED]: {
    title: 'Account executive assigned',
    ...formSecondary
  },
  [EventType.ACCOUNT_UPSELL]: {
    title: 'Account upsell updated',
    ...formSecondary
  },
  [EventType.ACCOUNT_MANUAL_APPROVAL_REQUIRED]: {
    title: 'Manual approval required',
    ...clockSecondary
  },
  [EventType.PROVIDER_CREATED]: {
    title: 'Provider created',
    ...formSecondary
  },
  [EventType.PROVIDER_ENABLED]: {
    title: 'Provider enabled',
    ...checkGreen
  },
  [EventType.PROVIDER_UPDATED]: {
    title: 'Provider updated',
    ...formSecondary
  },
  [EventType.PROVIDER_DISABLED]: {
    title: 'Provider disabled',
    ...infoRed
  },
  [EventType.ACCOUNT_RISK_PROFILE_UPDATED]: {
    title: 'Risk profile updated',
    ...infoSecondary
  },
  [EventType.ACCOUNT_RISK_CHECK_COMPLETED]: {
    title: 'Risk check completed',
    ...checkGreen
  }
};

const descriptionComponentMapping: Record<
  string,
  (value: string, event: AccountEventWithChanged) => JSX.Element | string | null
> = {
  [AccountKeysUpdate.NOTE]: (value, event) => (
    <Paragraph
      style={{
        whiteSpace: 'pre-line',
        marginBottom:
          event.type === EventType.ACCOUNT_NOTE_UPDATED ||
          event.type === EventType.ACCOUNT_MANUAL_APPROVAL_REQUIRED
            ? 0
            : 10
      }}>
      <Linkify options={{target: '_blank'}}>{value}</Linkify>
    </Paragraph>
  ),
  [AccountKeysUpdate.USER_NOTE]: (value) => (
    <div style={{whiteSpace: 'pre-line'}}>
      <Linkify options={{target: '_blank'}}>{value}</Linkify>
    </div>
  ),
  [AccountKeysUpdate.BILLINGPLAN]: (value) => <div>Plan: {value?.replace('_', ' ')}</div>,
  [AccountKeysUpdate.IBAN]: (value) => <div>IBAN: {formatIBAN(value, true)}</div>,
  [AccountKeysUpdate.PAYOUTTERMS_IBAN]: (value) => (
    <div>Payouts IBAN: {formatIBAN(value, true)}</div>
  ),
  [AccountKeysUpdate.PAYOUTTERMS_RESERVEPERCENT]: (value) => <div>Rolling reserve: {value} %</div>,
  [AccountKeysUpdate.PAYOUTTERMS_FIRSTPERIOD]: (value) => (
    <div>First payout period: {value} days</div>
  ),
  [AccountKeysUpdate.PAYOUTTERMS_PERIOD]: (value) => <div>Payout period: {value} days</div>,
  [AccountKeysUpdate.BUSINESS_LEGALNAME]: (value) => <div>Legal name: {value}</div>,
  [AccountKeysUpdate.BUSINESS_DOCUMENTNUMBER]: (value) => <div>CIF: {value}</div>,
  [AccountKeysUpdate.REPRESENTATIVES]: (value: any) => {
    const name =
      value.lastName || value.firstName ? (
        <div>
          Name: {value.lastName} {value.firstName}
        </div>
      ) : undefined;
    const nif = value.documentNumber ? <div>NIF: {value.documentNumber}</div> : undefined;
    return (
      <div>
        {name}
        {nif}
      </div>
    );
  },
  [AccountKeysUpdate.REASON]: (value) => (
    <div style={{whiteSpace: 'pre-line'}}>
      <Linkify options={{target: '_blank'}}>{value}</Linkify>
    </div>
  ),
  [AccountKeysUpdate.FEES_HIGHRISKFEE]: (value) => (
    <div>High risk fee: {formatPercent(Number(value || 0))}</div>
  ),
  [AccountKeysUpdate.DYNAMICBILLING]: (value) =>
    value ? <>Dynamic billing enabled</> : <>Dynamic billing disabled</>,
  [AccountKeysUpdate.CHURN_REASON]: (value) =>
    value !== 'Other' ? <Text strong>{churnReasonLabels[value as ChurnReason]}</Text> : null,
  [AccountKeysUpdate.CHURN_REASON_OPTION]: (value) =>
    value && value !== 'Other' ? <Text>&nbsp;({value})</Text> : null,
  [AccountKeysUpdate.STATUS_REASON]: (value, event) =>
    value !== 'OTHER' ? (
      <Paragraph strong style={{marginBottom: event.object.statusNote ? 10 : 0}}>
        {event.object.statusNote ? 'Reason: ' : ''}
        {statusReasonLabels[value as StatusReason]}
      </Paragraph>
    ) : null,
  [AccountKeysUpdate.STATUS_NOTE]: (value) => (
    <div style={{whiteSpace: 'pre-line'}}>
      <Linkify options={{target: '_blank'}}>{value}</Linkify>
    </div>
  ),
  [AccountKeysUpdate.CHURN_NOTE]: (value) => <Text>&nbsp;({value})</Text>,
  [AccountKeysUpdate.BLOCKED]: (value) => (
    <Text>{value ? 'Account blocked' : 'Account unblocked'}</Text>
  ),
  [AccountKeysUpdate.RISK_PROFILE]: (value, event) =>
    value && event.type === EventType.ACCOUNT_RISK_PROFILE_UPDATED ? (
      <Text>Risk profile: {humanize(value)}</Text>
    ) : null,
  [AccountKeysUpdate.RISK_PROFILE_NOTE]: (value, event) =>
    value && event.type === EventType.ACCOUNT_RISK_PROFILE_UPDATED ? (
      <div style={{whiteSpace: 'pre-line'}}>
        <Linkify options={{target: '_blank'}}>{value}</Linkify>
      </div>
    ) : null,
  [AccountKeysUpdate.RISK_IGNORED_KEYWORDS]: (_, event) =>
    event.type === EventType.ACCOUNT_RISK_PROFILE_UPDATED ? (
      <Text>Ignored keywords: {event.object.riskIgnoredKeywords?.join(', ')}</Text>
    ) : null,
  [AccountKeysUpdate.TAGS]: (_, event) =>
    event.type === EventType.ACCOUNT_TAGS_UPDATED ? (
      <Text>{event.object.tags?.join(', ')}</Text>
    ) : null,
  [AccountKeysUpdate.ACCOUNT_EXECUTIVE_ASSIGNED]: (value) => (
    <div>Account executive: {formatHumanName(value)}</div>
  ),
  [AccountKeysUpdate.ACCOUNT_UPSELL_EXECUTIVE]: (value) => (
    <div>Upsell executive: {formatHumanName(value)}</div>
  ),
  [AccountKeysUpdate.ACCOUNT_UPSELL_PAYMENT_METHODS]: (value, event) => {
    return (
      <div>Payment methods: {event.object.upsell!.paymentMethods.map(humanize).join(', ')}</div>
    );
  },
  [AccountKeysUpdate.ACCOUNT_UPSELL_DATE]: (value) => (
    <div>Date: {formatDate(value, moment.ISO_8601, true)}</div>
  )
};

export const getEventItem = (event: AccountEventWithChanged | ProviderEvent | ChargeEvent) => {
  return (
    eventMapping[event.type] || {
      title: 'Unknown event',
      ...infoSecondary
    }
  );
};

export const sortChangedKeys = (a: string, b: string) => {
  if (a.toLowerCase().includes('note')) return 1;
  if (b.toLowerCase().includes('note')) return -1;
  return 0;
};

export const getAccountEventDescription = (event: AccountEventWithChanged) => {
  const description = event.changed
    ?.map((change) => {
      const value = dot.pick(change, event.object);
      if (!isDefined(value)) return undefined;
      if (Array.isArray(value)) {
        return value[0] ? descriptionComponentMapping[change]?.(value[0], event) : undefined;
      } else {
        return descriptionComponentMapping[change]?.(value, event);
      }
    })
    .filter((value) => isDefined(value));
  return description?.length
    ? description.slice(0, 3).map((item, i) => <div key={i}>{item}</div>)
    : undefined;
};

export const getProviderEventDescription = (event: ProviderEvent) => {
  return (
    paymentProcessorLabels[event.object.processor as Processor] +
    ' - ' +
    paymentMethodLabels[event.object.paymentMethod as PaymentMethods]
  );
};

export const getChurnReason = (account: AccountFieldsFragment) => {
  if (!account.churnReason) return null;
  const reason = churnReasonLabels[account.churnReason];
  if (account.churnNote) {
    return `${reason} (${account.churnNote})`;
  }
  if (account.churnReasonOption) {
    return `${reason} (${account.churnReasonOption})`;
  }
  return reason;
};

type Item = MetadataConfigItem | MetadataItem;

export const getMetadataLabel = (item: Item) => {
  const labels: Record<string, string> = {
    'petitionParams.currency': 'Currency',
    'petitionParams.merchantCode': 'Merchant code',
    secret: 'Secret key (SHA256)',
    lwvExemption: 'Skip authentication for low value payments',
    traExemptionLimit: 'Skip authentication for payments below'
  };
  return labels[item.key] || item.label;
};

export const getMetadataHint = (item: Item) => {
  const hints: Record<string, string> = {
    lwvExemption: 'Skip 3D Secure Authentication (SCA) for payments below 30 EUR',
    traExemptionLimit:
      'Skip 3D Secure Authentication for payments below given amount. To enable this exemption you need to contact your acquiring bank. Transaction without 3D Secure Authentication or SCA is more prone to end up in a Chargeback. You need to be aware that you assume the amount of any payment which has a Chargeback, as the issuer bank will send back the money to the cardholder.'
  };
  return hints[item.key];
};

export const getIdStatusMessage = (statusCode: number | null | undefined) => {
  const messages: Record<number, string> = {
    101: 'Physical document not used',
    102: 'Suspected document tampering',
    103: 'Person showing the document does not appear to match document photo',
    105: 'Suspicious behaviour',
    106: 'Known fraud',
    108: 'Velocity/abuse duplicated end-user',
    109: 'Velocity/abuse duplicated device',
    110: 'Velocity/abuse duplicated ID',
    112: 'Restricted IP location',
    113: 'Suspicious behaviour - Identity Farming',
    120: 'Person on the portrait does not appear to match reference photo',
    121: 'User ID missing',
    122: 'No reference found',
    123: 'Unable to pass registry checks',
    126: 'Potential PEP match',
    127: 'Face match with blocklist',
    201: 'Video and/or photos missing',
    202: 'Face not clearly visible',
    203: 'Full document not visible',
    204: 'Poor image quality',
    205: 'Document damaged',
    206: 'Document type not supported',
    207: 'Document expired',
    502: 'Multiple parties present in session',
    503: 'Attempted deceit      ',
    504: 'Attempted deceit, device screen used',
    505: 'Attempted deceit, printout used ',
    507: 'Presented document tampered, data cross reference',
    508: 'Presented document tampered, document similarity to specimen',
    509: 'Person showing the document does not match document photo',
    510: 'Presented document type not supported',
    511: 'Presented document expired',
    515: 'Attempted deceit, device screen used for face image',
    526: 'Attempted deceit, photos streamed',
    527: 'Unable to collect proof of address data',
    528: 'Proof of address issue date too old',
    530: 'Person is under 13 years old',
    531: 'Person is under 14 years old',
    532: 'Person is under 16 years old',
    533: 'Person is under 18 years old',
    534: 'Person is under 20 years old',
    535: 'Person is under 21 years old',
    536: 'Person is under 25 years old',
    537: 'Unable to perform document media portrait cropping',
    539: 'Resubmission limit exceeded',
    540: 'Low confidence score for the session',
    541: 'Name on document does not match with name in session initialisation data',
    542: 'Unable to validate CPF',
    605: 'Face missing from image',
    606: 'Face is not clearly visible',
    608: 'Document front missing',
    619: 'Document data not visible',
    602: 'Presented document type not supported',
    603: 'Video missing',
    609: 'Document back missing',
    614: 'Document front not fully in frame',
    615: 'Document back not fully in frame',
    620: 'Presented document expired',
    621: 'Document annulled or damaged',
    625: 'Unable to collect surname',
    626: 'Unable to collect first names',
    627: 'Unable to collect date of birth',
    628: 'Unable to collect issue date',
    629: 'Unable to collect expiry date',
    630: 'Unable to collect gender',
    631: 'Unable to collect document number',
    632: 'Unable to collect personal number',
    633: 'Unable to collect nationality',
    634: 'Unable to collect home address',
    635: 'Document and face image missing',
    636: 'Presented document not real, screen used',
    637: 'Presented document not real, printout used',
    640: 'Person did not give consent',
    641: 'Multiple Faces Detected',
    642: 'Multiple Documents Uploaded',
    643: 'Unable to crop face image from a document front',
    644: 'Unable to collect Identificador de Ciudadano (INE)',
    645: 'Resubmit - Unable to collect OCR (IFE)',
    646: 'Unable to estimate age',
    647: 'Document not recognised',
    648: 'Technical issues',
    649: 'Unable to collect foreigner information',
    650: 'Unable to collect process number',
    651: 'Unable to collect occupation information',
    652: 'Unable to collect employer information',
    653: 'Unable to collect residence permit type',
    654: "Unable to collect driver's license number",
    655: 'Unable to collect additional name',
    657: 'Open passport image missing',
    901: 'Person is not listed as legal representative'
  };

  return statusCode ? messages[statusCode] : '';
};

export const checkDocNumberInRegistry = (docNumber: string | null | undefined) => {
  if (!docNumber) return false;
  const firstChar = docNumber[0];
  return firstChar === 'A' || firstChar === 'B';
};

export const getFullName = (rep?: Representative | null) => {
  if (!rep) return '';
  const name = [rep.firstName, rep.lastName].filter(Boolean);
  return name.join(' ');
};
